import { formatCount, formatPercent } from "./format";

const SEPARATOR = " · ";

// One line of text from `/social-proof/asset/:type/:id`. Empty parts are dropped;
// an empty string means "render nothing".
export function buildSocialProofLine(data, maxParts = 4) {
  if (!data || typeof data !== "object") return "";
  const parts = [];
  const listening = Number(data.listeningNow);
  if (listening > 0) parts.push(`${formatCount(listening)} listening now`);
  const saved = Number(data.savedCount);
  if (saved > 0) parts.push(`${formatCount(saved)} saved`);
  const completion = Number(data.completionPct);
  if (Number.isFinite(completion) && completion > 0) parts.push(`${formatPercent(completion)} completed`);
  const fans = Number(data.fansThisWeek);
  if (fans > 0) parts.push(`+${formatCount(fans)} fans this week`);
  const trending = Array.isArray(data.trendingIn) ? data.trendingIn.find((t) => t && t.label) : null;
  // The server already phrases this: scopeLabel returns "Trending in Rock" or
  // "Trending globally", never a bare genre. Wrapping it again is what put
  // "Trending in Trending in Rock" on the profile.
  if (trending) parts.push(trending.label);
  if (data.youWereFirst100) parts.push("You were among the first 100");
  return parts.slice(0, maxParts).join(SEPARATOR);
}

// In-memory TTL cache keyed by asset; dedupes in-flight requests so several
// cards on one page share a single fetch.
export function createSocialProofCache(ttlMs = 60000, now = () => Date.now()) {
  const entries = new Map();
  return {
    async get(type, id, fetcher) {
      const key = `${type}:${id}`;
      const hit = entries.get(key);
      if (hit && (hit.promise || now() - hit.at < ttlMs)) return hit.promise || hit.value;
      let started;
      try {
        started = Promise.resolve(fetcher());
      } catch (error) {
        return Promise.reject(error);
      }
      const promise = started
        .then((value) => {
          entries.set(key, { at: now(), value });
          return value;
        })
        .catch((error) => {
          entries.delete(key);
          throw error;
        });
      entries.set(key, { promise });
      return promise;
    },
    clear() {
      entries.clear();
    },
    size() {
      return entries.size;
    },
  };
}

export const socialProofCache = createSocialProofCache();
